/**
 * As-you-type dressing for a US phone box: whatever lands in the input, what shows
 * is "(305) 555-0142" taking shape — letters and stray symbols never appear at all
 * (a road-test finding on the driver form). Presentation only: the API's
 * PhoneNumber.Parse remains the authority on what is actually a valid number.
 */
export function formatPhoneTyping(previous: string, next: string): string {
  let digits = next.replace(/\D/g, '')

  // "+1 305 555 0142" pasted from a contact card is the same number with the
  // country code; the box keeps the ten NANP digits, like the API does.
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1)
  }

  // More digits than a NANP number has: truncating would quietly dial a different
  // contact, so the edit refuses to land instead — typing an eleventh digit does
  // nothing, and an overlong paste leaves the box as it was (review finding).
  if (digits.length > 10) {
    return previous
  }

  // Deleting a formatting character alone leaves the digits unchanged, and the
  // keystroke would appear to do nothing — so it eats the digit just before the
  // deletion point. The position matters: always trimming the END would let a
  // backspace over ")" mid-field corrupt the tail of the number (review finding).
  const previousDigits = previous.replace(/\D/g, '')
  if (next.length < previous.length && digits === previousDigits) {
    let firstDiff = 0
    while (firstDiff < next.length && next[firstDiff] === previous[firstDiff]) {
      firstDiff += 1
    }
    const digitsBefore = previous.slice(0, firstDiff).replace(/\D/g, '').length
    const at = Math.max(0, digitsBefore - 1)
    digits = digits.slice(0, at) + digits.slice(at + 1)
  }

  if (digits.length === 0) {
    return ''
  }
  if (digits.length <= 3) {
    return `(${digits}`
  }
  if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  }
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}
